import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Star, GitFork, Download, Copy, ExternalLink, Github, Package, Tag } from "lucide-react";
import { toast } from "sonner";

export default function RepositoryDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/repositories/${id}`).then((res) => setR(res.data)).catch(() => setNotFound(true));
  }, [id]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Perintah disalin");
  };

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center relative z-10">
      <h1 className="font-heading text-3xl font-bold">Repository tidak ditemukan</h1>
      <Link to="/repositories" className="btn-primary mt-6">Kembali ke katalog</Link>
    </div>
  );
  if (!r) return <div className="text-center py-24 text-slate-500">Memuat...</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
      <Link to="/repositories" className="text-magenta1 text-sm inline-flex items-center gap-2 mb-8" data-testid="back-to-repos">
        <ArrowLeft className="w-4 h-4" /> Kembali ke katalog
      </Link>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 card p-8">
          <div className="flex gap-2 mb-3 flex-wrap">
            <span className="chip chip-magenta">{r.category}</span>
            {r.language && <span className="chip chip-cyan">{r.language}</span>}
            {r.license && <span className="chip">{r.license}</span>}
          </div>
          <h1 className="font-heading text-4xl font-black" data-testid="repo-title">{r.name}</h1>
          <div className="font-mono text-xs text-slate-500 uppercase tracking-widest mt-1">{r.full_name}</div>
          <p className="text-slate-300 text-lg mt-4 leading-relaxed">{r.description}</p>

          {r.install_command && (
            <div className="mt-6">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan1 mb-2">Install Command</div>
              <div className="terminal">
                <span className="truncate">{r.install_command}</span>
                <button onClick={() => copy(r.install_command)} className="text-cyan1 hover:text-magenta1 shrink-0" data-testid="copy-install-detail">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-3 flex-wrap">
            <a href={r.github_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="detail-github">
              <Github className="w-4 h-4" /> Lihat di GitHub
            </a>
            {r.download_url && (
              <a href={r.download_url} target="_blank" rel="noreferrer" className="btn-primary" data-testid="detail-download">
                <Download className="w-4 h-4" /> Unduh ZIP
              </a>
            )}
            {r.homepage && (
              <a href={r.homepage} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="detail-homepage">
                <ExternalLink className="w-4 h-4" /> Homepage
              </a>
            )}
          </div>

          {r.tags?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center gap-2">
              <Tag className="w-4 h-4 text-slate-500" />
              {r.tags.map((t) => (
                <Link key={t} to={`/repositories?search=${encodeURIComponent(t)}`} className="chip hover:border-magenta1/50 hover:text-magenta1">#{t}</Link>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">GitHub Stats</div>
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400" /> Stars</span>
                <span className="font-heading text-xl font-bold text-white">{r.stars?.toLocaleString() || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm flex items-center gap-2"><GitFork className="w-4 h-4 text-cyan1" /> Forks</span>
                <span className="font-heading text-xl font-bold text-white">{r.forks?.toLocaleString() || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm flex items-center gap-2"><Package className="w-4 h-4 text-magenta1" /> Language</span>
                <span className="font-mono text-sm text-white">{r.language || "-"}</span>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Deployment</div>
            <div className="mt-3 text-sm text-slate-300 space-y-2">
              <div>✔ Linux (native / Docker)</div>
              <div>✔ macOS</div>
              <div>✔ Windows (WSL2)</div>
              <div>✔ Cloud (AWS · GCP · Azure)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
